import { MinusCircleIcon } from "@phosphor-icons/react/MinusCircle";
import { PlusCircleIcon } from "@phosphor-icons/react/PlusCircle";
import { Icon } from "../Icons/Icon";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface StepperProps {
  value: number;
  onChange: (value: number) => void;
  /** Smallest allowed value — the minus button disables here. */
  min?: number | undefined;
  /** Largest allowed value — the plus button disables here. */
  max?: number | undefined;
}

/**
 * A compact quantity stepper: − value + . Use for small integer counts
 * (handling units, line items) where a full NumberInput would be heavy.
 *
 * Both buttons carry the brand accent so that `disabled:opacity-30` has
 * something to fall from. A muted minus reads as permanently unavailable —
 * enabled and disabled land within a shade of each other, and on a dark surface
 * a faint token at 30% is barely a mark at all.
 */
export function Stepper({ value, onChange, min, max }: StepperProps) {
  const markDirty = useMarkDialogDirty();
  const atMin = min !== undefined && value <= min;
  const atMax = max !== undefined && value >= max;

  const set = (next: number) => {
    markDirty?.();
    onChange(next);
  };

  return (
    <div className="inline-flex items-center gap-3">
      <button
        type="button"
        aria-label="Decrease"
        disabled={atMin}
        onClick={() => set(value - 1)}
        className="cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-30"
      >
        <Icon
          icon={MinusCircleIcon}
          size="xl"
          weight="fill"
          color="text-brand-text"
        />
      </button>
      <span className="min-w-5 text-center text-2xl font-bold text-foreground tabular-nums">
        {value}
      </span>
      <button
        type="button"
        aria-label="Increase"
        disabled={atMax}
        onClick={() => set(value + 1)}
        className="cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-30"
      >
        <Icon
          icon={PlusCircleIcon}
          size="xl"
          weight="fill"
          color="text-brand-text"
        />
      </button>
    </div>
  );
}
